import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Eye, EyeOff, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState, formatDateTime } from "../ui";

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const blankForm = () => ({
  name: "",
  monthly_fee: 0,
  annual_fee: 0,
  description: "",
  active: true,
  order: 100,
});

export default function AdminTiposAssociados() {
  const [items, setItems] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/associate-types")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Não foi possível carregar os tipos."));

  useEffect(() => {
    load();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/associate-types", {
        ...form,
        monthly_fee: Number(form.monthly_fee) || 0,
        annual_fee: Number(form.annual_fee) || 0,
        order: Number(form.order) || 100,
      });
      toast.success("Tipo criado.");
      setOpenCreate(false);
      setForm(blankForm());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({
      name: t.name,
      monthly_fee: t.monthly_fee,
      annual_fee: t.annual_fee,
      description: t.description || "",
      active: !!t.active,
      order: t.order,
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      await api.patch(`/admin/associate-types/${editing.id}`, {
        ...editForm,
        monthly_fee: Number(editForm.monthly_fee) || 0,
        annual_fee: Number(editForm.annual_fee) || 0,
        order: Number(editForm.order) || 100,
      });
      toast.success("Tipo atualizado.");
      setEditing(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/associate-types/${t.id}`, {
        active: !t.active,
      });
      toast.success(t.active ? "Desativado." : "Reativado.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir o tipo "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/associate-types/${t.id}`);
      toast.success("Tipo excluído.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-tipos">
      <PageHeader
        title="Tipos de Associados & Contribuições"
        subtitle="Configure os tipos de associação aceitos pela ABLEMP e os respectivos valores de contribuição (mensal e anual). As mudanças refletem imediatamente no formulário de cadastro."
        actions={
          <button
            data-testid="new-tipo-btn"
            onClick={() => {
              setOpenCreate((v) => !v);
              setForm(blankForm());
            }}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Novo tipo
          </button>
        }
      />

      {openCreate && (
        <Card className="p-5 mb-6">
          <TipoForm
            form={form}
            setForm={setForm}
            onSubmit={submitCreate}
            onCancel={() => setOpenCreate(false)}
            busy={busy}
            submitLabel="Criar tipo"
          />
        </Card>
      )}

      {items === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum tipo cadastrado"
          hint="Crie o primeiro tipo de associação no botão acima."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3 w-12">Ordem</th>
                  <th className="text-left p-3">Nome</th>
                  <th className="text-left p-3">Descrição</th>
                  <th className="text-right p-3">Mensal</th>
                  <th className="text-right p-3">Anual</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`tipo-row-${t.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="p-3 text-slate-500 font-mono">{t.order}</td>
                    <td className="p-3">
                      <div className="font-medium">{t.name}</div>
                      {!t.active && (
                        <div className="text-xs text-slate-400 italic">
                          Não aparece no cadastro
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-slate-600 max-w-md">
                      <div className="line-clamp-2">{t.description || "—"}</div>
                    </td>
                    <td className="p-3 text-right font-medium">
                      {t.monthly_fee > 0 ? (
                        <span className="text-[#002776]">
                          {formatBRL(t.monthly_fee)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-medium">
                      {t.annual_fee > 0 ? (
                        <span className="text-[#002776]">
                          {formatBRL(t.annual_fee)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {t.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px]">
                          <Eye size={11} /> Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-100 text-slate-600 text-[11px]">
                          <EyeOff size={11} /> Inativo
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`tipo-edit-${t.id}`}
                          onClick={() => openEdit(t)}
                          title="Editar"
                          className="px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wide hover:border-[#002776]"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          data-testid={`tipo-toggle-${t.id}`}
                          onClick={() => toggleActive(t)}
                          title={t.active ? "Desativar" : "Reativar"}
                          className={`inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-wide ${
                            t.active
                              ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                              : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          }`}
                        >
                          {t.active ? (
                            <>
                              <EyeOff size={12} /> Desativar
                            </>
                          ) : (
                            <>
                              <Eye size={12} /> Reativar
                            </>
                          )}
                        </button>
                        <button
                          data-testid={`tipo-delete-${t.id}`}
                          onClick={() => remove(t)}
                          title="Excluir"
                          className="px-2 py-1 border border-rose-300 text-rose-700 text-[10px] uppercase tracking-wide hover:bg-rose-50"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-6 p-4 bg-[#FEDF00]/15 border border-[#FEDF00]/40 rounded-sm text-sm text-[#0A0F14] flex items-start gap-3">
        <BadgeDollarSign size={18} className="shrink-0 mt-0.5 text-[#002776]" />
        <div>
          <div className="font-semibold">Importante</div>
          <p className="text-slate-700 mt-0.5">
            Os valores configurados aqui aparecem no formulário público de
            cadastro e podem ser usados como referência ao gerar boletos. Tipos
            inativos não são oferecidos no cadastro, mas continuam vinculados a
            associados existentes. Para excluir um tipo em uso, reassocie os
            usuários primeiro.
          </p>
        </div>
      </div>

      {editing && editForm && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4 overflow-auto">
          <div className="bg-white rounded-md w-full max-w-lg p-6 my-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  Editar tipo
                </div>
                <h3 className="mt-1 font-serif-display text-2xl">
                  {editing.name}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Atualizado em {formatDateTime(editing.updated_at)}
                </p>
              </div>
              <button
                onClick={() => {
                  setEditing(null);
                  setEditForm(null);
                }}
                className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <TipoForm
              form={editForm}
              setForm={setEditForm}
              onSubmit={submitEdit}
              onCancel={() => {
                setEditing(null);
                setEditForm(null);
              }}
              busy={busy}
              submitLabel="Salvar alterações"
              isEdit
            />
          </div>
        </div>
      )}
    </div>
  );
}

const TipoForm = ({ form, setForm, onSubmit, onCancel, busy, submitLabel }) => (
  <form
    data-testid="tipo-form"
    onSubmit={onSubmit}
    className="grid md:grid-cols-2 gap-4"
  >
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Nome do tipo *
      </label>
      <input
        data-testid="tipo-name"
        required
        minLength={2}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
        placeholder="Ex.: Contribuinte Titular, Honorário, Patrocinador..."
      />
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Contribuição mensal (R$)
      </label>
      <input
        data-testid="tipo-monthly"
        type="number"
        step="0.01"
        min="0"
        value={form.monthly_fee}
        onChange={(e) =>
          setForm({ ...form, monthly_fee: e.target.value })
        }
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
      />
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Contribuição anual (R$)
      </label>
      <input
        data-testid="tipo-annual"
        type="number"
        step="0.01"
        min="0"
        value={form.annual_fee}
        onChange={(e) => setForm({ ...form, annual_fee: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
      />
    </div>
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Descrição
      </label>
      <textarea
        data-testid="tipo-description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[80px]"
        placeholder="Quem pode se enquadrar nesse tipo? Quais direitos/deveres?"
      />
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Ordem de exibição
      </label>
      <input
        data-testid="tipo-order"
        type="number"
        min="0"
        max="9999"
        value={form.order}
        onChange={(e) => setForm({ ...form, order: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
      />
    </div>
    <div className="flex items-end">
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          data-testid="tipo-active"
          type="checkbox"
          checked={form.active}
          onChange={(e) => setForm({ ...form, active: e.target.checked })}
          className="h-4 w-4"
        />
        Ativo (exibido no cadastro)
      </label>
    </div>
    <div className="md:col-span-2 flex gap-2">
      <button
        disabled={busy}
        data-testid="tipo-submit"
        className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
      >
        {busy ? "Salvando..." : submitLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
      >
        Cancelar
      </button>
    </div>
  </form>
);
